import React, { Component } from "react";
import PropTypes from "prop-types";
import { makeStyles, withStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import IconButton from "@material-ui/core/IconButton";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import LastPageIcon from "@material-ui/icons/LastPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { isNotEmpty } from "../../utils/Validator";
import "../../table.css";

const headCells = [
  {
    id: "prvTaxId",
    fid: "prvTaxId",
    label: "Tax ID Number (TIN)",
    filterLabel: "Tax ID Number (TIN)",
    width: "12rem",
  },
  {
    id: "prvId",
    fid: "prvId",
    label: "Provider ID Number",
    filterLabel: "Provider ID Number",
    width: "12rem",
  },
  {
    id: "prvName",
    fid: "prvName",
    label: "Provider Name",
    filterLabel: "Provider Name",
    width: "20rem",
  },
  {
    id: "prvProviderTypeDisplay",
    fid: "prvProviderTypeDisplay",
    label: "Provider Type",
    filterLabel: "Provider Type",
    width: "14rem",
  },
  {
    id: "pstatStatus",
    fid: "pstatStatus",
    label: "Provider Status",
    filterLabel: "Provider Status",
    width: "10rem",
  },
];

const EMPTY_FILTERS = headCells.reduce(
  (filters, headCell) => ({ ...filters, [headCell.fid]: "" }),
  {}
);

function desc(a, b, orderBy) {
  let c = a[orderBy];
  let d = b[orderBy];

  if (isNotEmpty(c) && typeof c === "string") {
    c = c.toUpperCase();
  }
  if (isNotEmpty(d) && typeof d === "string") {
    d = d.toUpperCase();
  }

  if (c === undefined || c === null) {
    c = "";
  }
  if (d === undefined || d === null) {
    d = "";
  }

  if (d < c) {
    return -1;
  }
  if (d > c) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });

  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort, onRequestFilter, filters } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const onFilter = (property) => (event) => {
    onRequestFilter(event.target.value, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ width: headCell.width }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={order}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
      <TableRow className="filter-row">
        {headCells.map((headCell) => (
          <TableCell
            key={`${headCell.id}-filter`}
            style={{ maxWidth: headCell.width, paddingBottom: 0 }}
          >
            <div style={{ paddingRight: "1rem", verticalAlign: "bottom" }}>
              <TextField
                id={`filter-${headCell.fid}`}
                fullWidth
                style={{ height: ".7" }}
                placeholder="Filter…"
                margin="dense"
                variant="outlined"
                value={filters[headCell.fid]}
                onChange={onFilter(headCell.fid)}
              />
            </div>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onRequestFilter: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  filters: PropTypes.object.isRequired,
};

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

function TablePaginationActions(props) {
  const classes = useStyles1();
  const theme = useTheme();

  const { count, page, rowsPerPage } = props;
  const onPageChange = props.onPageChange || props.onChangePage;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

const styles = (theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  tableWrapper: {
    overflowX: "auto",
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
});

class ProviderSearchTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      order: "asc",
      orderBy: "prvName",
      page: 0,
      rowsPerPage: 10,
      filters: { ...EMPTY_FILTERS },
    };
  }

  handleRequestSort = (event, property) => {
    const isDesc =
      this.state.orderBy === property && this.state.order === "desc";
    this.setState({ order: isDesc ? "asc" : "desc", orderBy: property });
  };

  handleRequestFilter = (value, property) => {
    const newFilters = { ...this.state.filters, [property]: value };
    this.setState({ filters: newFilters, page: 0 });

    const filteredProviders = this.props.unfilteredProviders.filter((provider) =>
      Object.entries(newFilters).every(([key, filterValue]) => {
        if (!filterValue) {
          return true;
        }

        const fieldValue = provider[key];
        if (fieldValue === null || fieldValue === undefined) {
          return false;
        }

        return String(fieldValue)
          .toUpperCase()
          .includes(filterValue.toUpperCase());
      })
    );

    this.props.setFilter(filteredProviders);
  };

  handleClearFilters = () => {
    this.setState({ filters: { ...EMPTY_FILTERS }, page: 0 });
    this.props.setFilter(this.props.unfilteredProviders);
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  render() {
    const { classes, providers } = this.props;
    const hasActiveFilters = Object.values(this.state.filters).some(
      (value) => value !== ""
    );

    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
            component="div"
            count={providers.length}
            rowsPerPage={this.state.rowsPerPage}
            page={this.state.page}
            ActionsComponent={TablePaginationActions}
            onPageChange={this.handleChangePage}
            onRowsPerPageChange={this.handleChangeRowsPerPage}
            labelRowsPerPage={
              hasActiveFilters ? (
                <>
                  <Button
                    size="medium"
                    variant="outlined"
                    onClick={this.handleClearFilters}
                    style={{ marginRight: "1rem" }}
                  >
                    Clear Filters
                  </Button>
                  Rows per page:
                </>
              ) : (
                "Rows per page:"
              )
            }
          />
          <div className={classes.tableWrapper}>
            <Table className={classes.table} aria-label="Provider search results table" size="small">
              <EnhancedTableHead
                classes={classes}
                order={this.state.order}
                orderBy={this.state.orderBy}
                onRequestSort={this.handleRequestSort}
                onRequestFilter={this.handleRequestFilter}
                filters={this.state.filters}
              />
              <TableBody>
                {stableSort(providers, getSorting(this.state.order, this.state.orderBy))
                  .slice(
                    this.state.page * this.state.rowsPerPage,
                    this.state.page * this.state.rowsPerPage + this.state.rowsPerPage
                  )
                  .map((row, index) => (
                    <TableRow hover tabIndex={-1} key={`${row.prvId}-${index}`}>
                      <TableCell>{row.prvTaxId}</TableCell>
                      <TableCell>{row.prvId}</TableCell>
                      <TableCell>{row.prvName}</TableCell>
                      <TableCell>{row.prvProviderTypeDisplay}</TableCell>
                      <TableCell>{row.pstatStatus}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
                    count={providers.length}
                    rowsPerPage={this.state.rowsPerPage}
                    page={this.state.page}
                    SelectProps={{
                      inputProps: { "aria-label": "rows per page" },
                      native: true,
                    }}
                    onPageChange={this.handleChangePage}
                    onRowsPerPageChange={this.handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </Paper>
      </div>
    );
  }
}

ProviderSearchTable.propTypes = {
  providers: PropTypes.array.isRequired,
  unfilteredProviders: PropTypes.array.isRequired,
  setFilter: PropTypes.func.isRequired,
};

export default withStyles(styles)(ProviderSearchTable);
